import { type EditableTag, TagModal } from "@/components/modal/TagModal";
import type { ProjectGroupItem } from "@/lib/hooks/projects/useProjectGroups";
import { useTags } from "@/lib/hooks/tags/useTags";
import { Button, Checkbox, FormField, Modal } from "@ai-teach/ui-web";
import { useEffect, useState } from "react";
import { HiPencil, HiPlus } from "react-icons/hi";

export interface ProjectGroupDraft {
  name: string;
  tagIds: string[];
  randomiseOrder: boolean;
}

interface ProjectGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Omit to create. Passing a group switches the modal to editing that group. */
  group?: ProjectGroupItem | null;
  saving: boolean;
  onSubmit: (draft: ProjectGroupDraft) => Promise<unknown>;
}

export function ProjectGroupModal({
  isOpen,
  onClose,
  group,
  saving,
  onSubmit,
}: ProjectGroupModalProps) {
  const isEditing = Boolean(group);
  const { tags } = useTags();

  const [name, setName] = useState("");
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [randomiseOrder, setRandomiseOrder] = useState(false);
  const [tagModal, setTagModal] = useState<{ tag: EditableTag | null } | null>(null);

  // The modal stays mounted between openings, so its fields are seeded from `group` each time
  // rather than from initial state.
  useEffect(() => {
    if (!isOpen) return;
    setName(group?.name ?? "");
    setTagIds([...(group?.tagIds ?? [])]);
    setRandomiseOrder(group?.randomiseOrder ?? false);
  }, [isOpen, group]);

  const toggleTag = (tagId: string) =>
    setTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );

  // A tag created from here is meant for the group being edited; a rename is not.
  const handleTagSaved = (tag: EditableTag) => {
    if (tagModal?.tag) return;
    setTagIds((prev) => (prev.includes(tag.id) ? prev : [...prev, tag.id]));
  };

  // A group with no tags would claim no slides, so at least one is required.
  const canSubmit = name.trim() !== "" && tagIds.length > 0 && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    // Left open on failure so the editor can retry without retyping.
    const saved = await onSubmit({ name: name.trim(), tagIds, randomiseOrder }).catch(() => null);
    if (saved === null) return;

    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      // Escape and the overlay must dismiss the tag modal on top, not this one underneath it.
      onClose={tagModal ? () => setTagModal(null) : onClose}
      closeOnOverlayClick={tagModal === null}
      title={isEditing ? "Edit Group" : "New Group"}
      size="md"
    >
      <div className="space-y-4">
        <FormField
          id="groupName"
          name="groupName"
          label="Name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Day 1"
          disabled={saving}
        />

        <div>
          <span className="block label">
            <span className="text-red-500 ml-1">* </span>Tags
          </span>
          <div className="flex flex-wrap gap-2 mt-2">
            {tags.map((tag) => {
              const selected = tagIds.includes(tag.id);
              return (
                // The edit control is a sibling, not a child: a button cannot nest in a button.
                <div key={tag.id} className="relative group">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => toggleTag(tag.id)}
                    className={`h-9 pl-4 pr-8 rounded-full border label whitespace-nowrap transition-colors flex items-center gap-2 ${selected ? "text-white shadow-sm" : "text-secondary-200 hover:bg-black/5 dark:hover:bg-white/5"} ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
                    style={{
                      borderColor: tag.color,
                      backgroundColor: selected ? tag.color : undefined,
                    }}
                    aria-pressed={selected}
                  >
                    {!selected && (
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                    )}
                    {tag.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => setTagModal({ tag })}
                    aria-label={`Edit ${tag.name}`}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity ${selected ? "text-white" : "text-gray-500 hover:text-accent-500"}`}
                  >
                    <HiPencil className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}

            <button
              type="button"
              onClick={() => setTagModal({ tag: null })}
              className="h-9 px-4 rounded-full border border-dashed border-gray-300 dark:border-gray-700 label whitespace-nowrap text-gray-500 hover:border-accent-500 hover:text-accent-500 transition-colors flex items-center gap-1.5"
            >
              <HiPlus className="w-4 h-4" />
              Add new tag
            </button>
          </div>
          <p className="caption text-gray-500 mt-2">
            Every slide carrying one of these tags falls into this group.
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm text-secondary-200 cursor-pointer">
          <Checkbox
            checked={randomiseOrder}
            onChange={(checked) => setRandomiseOrder(checked)}
            size="sm"
            disabled={saving}
          />
          Randomise slide order
        </label>

        <div className="flex items-center justify-between pt-4 border-t border-gray-800">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            {saving ? "Saving..." : isEditing ? "Save Group" : "Create Group"}
          </Button>
        </div>
      </div>

      <TagModal
        isOpen={tagModal !== null}
        onClose={() => setTagModal(null)}
        tag={tagModal?.tag}
        onSaved={handleTagSaved}
      />
    </Modal>
  );
}
